import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { type FC, useCallback, useEffect, useState } from 'react';
import { Badge } from 'react-bootstrap';
import Dropdown from 'react-bootstrap/Dropdown';
import {
  ArrowUpCircle,
  ClockHistory,
  Eye,
  Lock,
  SlashCircle,
} from 'react-bootstrap-icons';
import { config } from '../../../modules/config';
import { TemporaryAccessCodeStatus } from '../../../modules/enums/status';
import { AccessCodeKind } from '../../../modules/enums/types';
import { useAuth, useNotifications } from '../../../modules/hooks';
import type { AccessCodeRecord } from '../../../modules/interfaces';
import { DotDropDown } from '../../Customize/DropDown';
import type { AccessCodesFilters } from '../../Dashboards/TemporaryAccessCodes/useAccessCodes';
import { useAccessCodes } from '../../Dashboards/TemporaryAccessCodes/useAccessCodes';
import {
  FilterBar,
  type FilterField,
  type FilterValues,
  getInitialFilterValues,
} from '../../Filters/FilterBar';
import { AccessCodeModal } from '../../Modals/AccessCode';
import { EditLocksModal } from '../../Modals/AccessCode/EditLocksModal';
import { ExtendModal } from '../../Modals/AccessCode/ExtendModal';
import { PromoteModal } from '../../Modals/AccessCode/PromoteModal';
import { ViewCodeModal } from '../../Modals/AccessCode/ViewCodeModal';
import { ConfirmModal, type ModalState } from '../../Modals/Confirm';
import { DataTable } from '../DataTable';

const ACCESS_CODE_SUBJECT = 'rooms';

const formatDate = (value?: string): string =>
  value ? DateTime.fromISO(value).toFormat('MM-dd-yyyy HH:mm') : '';

// Filter config for the shared FilterBar. The table owns the domain knowledge (keys,
// labels, enum values); the bar itself stays agnostic. Status defaults to Enabled.
const FILTER_FIELDS: FilterField[] = [
  {
    key: 'kind',
    allLabel: 'All types',
    ariaLabel: 'Filter by type',
    options: [
      { value: AccessCodeKind.TEMPORARY, label: 'Temporary' },
      { value: AccessCodeKind.RESERVATION, label: 'Reservation' },
    ],
  },
  {
    key: 'status',
    allLabel: 'All statuses',
    ariaLabel: 'Filter by status',
    defaultValue: TemporaryAccessCodeStatus.ENABLED,
    options: [
      { value: TemporaryAccessCodeStatus.ENABLED, label: 'Enabled' },
      { value: TemporaryAccessCodeStatus.DISABLED, label: 'Disabled' },
    ],
  },
];

interface CodeDotMenuProps {
  data?: AccessCodeRecord;
  onView: (record: AccessCodeRecord) => void;
  onExtend: (record: AccessCodeRecord) => void;
  onPromote: (record: AccessCodeRecord) => void;
  onEditLocks: (record: AccessCodeRecord) => void;
  onDisable: (record: AccessCodeRecord) => void;
}

const CodeDotMenu: FC<CodeDotMenuProps> = ({
  data,
  onView,
  onExtend,
  onPromote,
  onEditLocks,
  onDisable,
}) => {
  if (!data) return null;
  const isTemporary = data.kind === AccessCodeKind.TEMPORARY;
  const isDisabled = data.status === TemporaryAccessCodeStatus.DISABLED;
  return (
    <DotDropDown title="" drop="end">
      <Dropdown.Item onClick={() => onView(data)}>
        <Eye className="dropdown-icon me-1" />
        View code
      </Dropdown.Item>
      {/* Extend/Promote are temporary-only. Disabled codes are view-only (no re-enable flow
          yet), so hide every action for them. */}
      {isTemporary && !isDisabled && (
        <>
          <Dropdown.Item onClick={() => onExtend(data)}>
            <ClockHistory className="dropdown-icon me-1" />
            Extend
          </Dropdown.Item>
          <Dropdown.Item onClick={() => onPromote(data)}>
            <ArrowUpCircle className="dropdown-icon me-1" />
            Promote
          </Dropdown.Item>
        </>
      )}
      {/* Edit locks + Disable apply to any enabled code, regardless of kind. */}
      {!isDisabled && (
        <>
          <Dropdown.Item onClick={() => onEditLocks(data)}>
            <Lock className="dropdown-icon me-1" />
            Edit locks
          </Dropdown.Item>
          <Dropdown.Item onClick={() => onDisable(data)}>
            <SlashCircle className="dropdown-icon me-1" />
            Disable
          </Dropdown.Item>
        </>
      )}
    </DotDropDown>
  );
};

export const AccessCodesTable: FC = () => {
  const { setSimpleToasts } = useNotifications()!;
  const { credentialsInfo } = useAuth()!;
  const accessCodes = useAccessCodes();

  const [codes, setCodes] = useState<AccessCodeRecord[]>([]);
  // Seeded from the FilterBar's own defaults so the first load matches its initial
  // selection (status = Enabled). Updated only when the user clicks Search.
  const [searchValues, setSearchValues] = useState<FilterValues>(() =>
    getInitialFilterValues(FILTER_FIELDS),
  );
  const [showCreate, setShowCreate] = useState(false);
  const [showView, setShowView] = useState(false);
  const [showExtend, setShowExtend] = useState(false);
  const [showPromote, setShowPromote] = useState(false);
  const [showEditLocks, setShowEditLocks] = useState(false);
  const [activeUuid, setActiveUuid] = useState<string>();
  const [confirmModal, setConfirmModal] = useState<ModalState>({
    show: false,
    title: '',
    body: '',
    handler: () => {},
  });

  const loadCodes = useCallback(async () => {
    if (!credentialsInfo?.token) return;
    const filters: AccessCodesFilters = {};
    if (searchValues.kind) filters.kind = searchValues.kind as AccessCodeKind;
    if (searchValues.status) {
      filters.status = searchValues.status as TemporaryAccessCodeStatus;
    }
    try {
      setCodes(await accessCodes.list(Object.keys(filters).length ? filters : undefined));
    } catch {
      setSimpleToasts({
        type: 'danger',
        message: 'Failed to load access codes',
        show: true,
      });
    }
  }, [credentialsInfo?.token, accessCodes, searchValues, setSimpleToasts]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  const onView = useCallback((record: AccessCodeRecord) => {
    setActiveUuid(record.uuid);
    setShowView(true);
  }, []);

  const onExtend = useCallback((record: AccessCodeRecord) => {
    setActiveUuid(record.uuid);
    setShowExtend(true);
  }, []);

  const onPromote = useCallback((record: AccessCodeRecord) => {
    setActiveUuid(record.uuid);
    setShowPromote(true);
  }, []);

  const onEditLocks = useCallback((record: AccessCodeRecord) => {
    setActiveUuid(record.uuid);
    setShowEditLocks(true);
  }, []);

  const onDisable = useCallback(
    (record: AccessCodeRecord) => {
      setConfirmModal({
        show: true,
        title: 'Disable this access code?',
        body: 'This revokes the credential at the provider so it can no longer open the locks. This can’t be undone.',
        handler: async () => {
          setConfirmModal((prev) => ({ ...prev, show: false }));
          await accessCodes.remove(record.uuid);
          await loadCodes();
          setSimpleToasts({
            type: 'success',
            message: 'Access code disabled',
            show: true,
          });
        },
      });
    },
    [accessCodes, loadCodes, setSimpleToasts],
  );

  const [columns] = useState<ColDef<AccessCodeRecord>[]>([
    {
      headerName: 'Label',
      flex: 3,
      valueGetter: (params) => params.data?.name ?? '—',
    },
    {
      headerName: 'Type',
      flex: 2,
      cellRenderer: (params: ICellRendererParams<AccessCodeRecord>) =>
        params.data?.kind === AccessCodeKind.RESERVATION ? (
          <Badge className="status-badge" bg="info">
            Reservation
          </Badge>
        ) : (
          <Badge className="status-badge" bg="warning" text="dark">
            Temporary
          </Badge>
        ),
    },
    {
      headerName: 'Status',
      flex: 2,
      cellRenderer: (params: ICellRendererParams<AccessCodeRecord>) => (
        <Badge
          className="status-badge"
          bg={
            params.data?.status === TemporaryAccessCodeStatus.ENABLED
              ? 'primary'
              : 'danger'
          }
        >
          {params.data?.status}
        </Badge>
      ),
    },
    {
      headerName: 'Locks',
      flex: 1,
      valueGetter: (params) => params.data?.locks?.length ?? 0,
    },
    {
      headerName: 'Start',
      flex: 2,
      valueGetter: (params) => formatDate(params.data?.startDate),
    },
    {
      headerName: 'End',
      flex: 2,
      valueGetter: (params) => formatDate(params.data?.endDate),
    },
    {
      headerName: 'ACTION',
      colId: 'action',
      cellClass: 'actionCol justify-content-end',
      flex: 1,
      cellRenderer: (params: ICellRendererParams<AccessCodeRecord>) => (
        <CodeDotMenu
          data={params.data}
          onView={onView}
          onExtend={onExtend}
          onPromote={onPromote}
          onEditLocks={onEditLocks}
          onDisable={onDisable}
        />
      ),
    },
  ]);

  return (
    <>
      <div className="my-custom-table">
        <DataTable
          addButtonFunction={() => setShowCreate(true)}
          addButtonText="Add Access Code"
          component={ACCESS_CODE_SUBJECT}
          leftSideContent={
            <FilterBar fields={FILTER_FIELDS} onSearch={setSearchValues} />
          }
          refreshHandler={loadCodes}
          changeState={0}
          rowData={codes}
          columnDefs={columns}
          pagination
          paginationPageSize={config.tables.balances.paginationSize}
        />
      </div>

      <AccessCodeModal
        show={showCreate}
        handleClose={() => setShowCreate(false)}
        onSaved={loadCodes}
      />
      <ViewCodeModal
        show={showView}
        uuid={activeUuid}
        handleClose={() => setShowView(false)}
      />
      <ExtendModal
        show={showExtend}
        uuid={activeUuid}
        handleClose={() => setShowExtend(false)}
        onSaved={loadCodes}
      />
      <PromoteModal
        show={showPromote}
        uuid={activeUuid}
        handleClose={() => setShowPromote(false)}
        onSaved={loadCodes}
      />
      <EditLocksModal
        show={showEditLocks}
        uuid={activeUuid}
        handleClose={() => setShowEditLocks(false)}
        onSaved={loadCodes}
      />
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        body={confirmModal.body}
        handleCancel={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
        handleConfirm={() => confirmModal.handler?.()}
      />
    </>
  );
};
